import type { CatalogProduct } from '../types'
import { catalogOrigin } from '../config'

export const sampleCatalog: CatalogProduct[] = [
  {
    id: 'sample-product-01',
    variantId: 'sample-variant-01',
    slug: 'yohaku-no-katachi',
    name: 'ダミーオブジェ 01',
    price: 4620,
    shortDescription: 'ダミーテキスト。静かな場所に置くためのオブジェ。',
    description:
      'ダミーテキスト。商品説明は管理画面から編集できます。実際の素材、サイズ、制作背景は公開前に登録してください。',
    material: '設定から登録',
    dimensions: '設定から登録',
    badges: ['一点もの'],
    tone: 'dawn',
    imageUrl: '/media/mudaii-temporary-object-v1.png',
    gallery: ['/media/mudaii-temporary-object-v1.png']
  },
  {
    id: 'sample-product-02',
    variantId: 'sample-variant-02',
    slug: 'mado-be-no-shirushi',
    name: 'ダミーオブジェ 02',
    price: 3180,
    shortDescription: 'ダミーテキスト。光を待つための小さなかたち。',
    description:
      'ダミーテキスト。商品説明は管理画面から編集できます。実際の素材、サイズ、制作背景は公開前に登録してください。',
    material: '設定から登録',
    dimensions: '設定から登録',
    badges: ['数量限定'],
    tone: 'clay',
    imageUrl: '/media/mudaii-temporary-object-v1.png',
    gallery: ['/media/mudaii-temporary-object-v1.png']
  },
  {
    id: 'sample-product-03',
    variantId: 'sample-variant-03',
    slug: 'chiisana-toomawari',
    name: 'ダミーオブジェ 03',
    price: 5940,
    shortDescription: 'ダミーテキスト。まっすぐ進まない日のために。',
    description:
      'ダミーテキスト。商品説明は管理画面から編集できます。実際の素材、サイズ、制作背景は公開前に登録してください。',
    material: '設定から登録',
    dimensions: '設定から登録',
    badges: ['予約販売'],
    tone: 'mist',
    imageUrl: '/media/mudaii-temporary-object-v1.png',
    gallery: ['/media/mudaii-temporary-object-v1.png']
  },
  {
    id: 'sample-product-04',
    variantId: 'sample-variant-04',
    slug: 'nemuru-ishi',
    name: 'ダミーオブジェ 04',
    price: 2860,
    shortDescription: 'ダミーテキスト。机のすみに置くためのかたち。',
    description:
      'ダミーテキスト。商品説明は管理画面から編集できます。実際の素材、サイズ、制作背景は公開前に登録してください。',
    material: '設定から登録',
    dimensions: '設定から登録',
    badges: [],
    tone: 'moss',
    imageUrl: '/media/mudaii-temporary-object-v1.png',
    gallery: ['/media/mudaii-temporary-object-v1.png']
  }
]

export const productBySlug = (slug: string) =>
  sampleCatalog.find((product) => product.slug === slug)

export const productByVariant = (variantId: string) =>
  sampleCatalog.find((product) => product.variantId === variantId)

type PublishedVariant = {
  id?: unknown
  name?: unknown
  price?: unknown
}

type PublishedProduct = {
  id?: unknown
  slug?: unknown
  name?: unknown
  short_description?: unknown
  description?: unknown
  badges?: unknown
  variants?: unknown
}

type PublishedCatalog = {
  products?: unknown
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function badgeValues(value: unknown): CatalogProduct['badges'] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (badge): badge is CatalogProduct['badges'][number] =>
      badge === '一点もの' || badge === '数量限定' || badge === '予約販売'
  )
}

function mapPublishedProduct(value: unknown, index: number): CatalogProduct | null {
  const record = asRecord(value)
  if (!record) return null
  const product = record as PublishedProduct
  const id = stringValue(product.id)
  const slug = stringValue(product.slug)
  const name = stringValue(product.name)
  const shortDescription = stringValue(product.short_description)
  const description = stringValue(product.description)
  if (!id || !slug || !name || !shortDescription || !description || !Array.isArray(product.variants)) return null
  const variant = product.variants
    .map((candidate) => asRecord(candidate))
    .filter((candidate): candidate is Record<string, unknown> => candidate !== null)
    .map((candidate) => candidate as PublishedVariant)
    .find((candidate) => stringValue(candidate.id) && typeof candidate.price === 'number' && Number.isInteger(candidate.price) && candidate.price >= 0)
  if (!variant || !stringValue(variant.id) || typeof variant.price !== 'number') return null
  const tones: CatalogProduct['tone'][] = ['dawn', 'clay', 'mist', 'moss']
  const imageUrl = stringValue(record.image_url) || '/media/mudaii-temporary-object-v1.png'
  const galleryValue: unknown = record.gallery
  const gallery = Array.isArray(galleryValue)
    ? galleryValue.filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
    : [imageUrl]
  return {
    id,
    variantId: stringValue(variant.id)!,
    slug,
    name,
    price: variant.price,
    shortDescription,
    description,
    material: '商品詳細ページにて公開',
    dimensions: '商品詳細ページにて公開',
    badges: badgeValues(product.badges),
    tone: tones[index % tones.length],
    imageUrl,
    gallery: gallery.length > 0 ? gallery : [imageUrl]
  }
}

export async function loadPublishedCatalog(): Promise<CatalogProduct[]> {
  if (!catalogOrigin) return sampleCatalog
  const manifestResponse = await fetch(catalogOrigin + '/catalog/manifest.json', {
    credentials: 'omit',
    headers: { accept: 'application/json' }
  })
  if (!manifestResponse.ok) throw new Error('catalog manifest request failed')
  const manifest = asRecord(await manifestResponse.json())
  const releaseId = manifest ? stringValue(manifest.current_release) : null
  if (!releaseId || !/^[A-Za-z0-9_-]{8,160}$/.test(releaseId)) {
    throw new Error('catalog manifest is invalid')
  }
  const catalogResponse = await fetch(
    catalogOrigin + '/catalog/releases/' + encodeURIComponent(releaseId) + '/catalog.json',
    { credentials: 'omit', headers: { accept: 'application/json' } }
  )
  if (!catalogResponse.ok) throw new Error('catalog release request failed')
  const catalog = await catalogResponse.json() as PublishedCatalog
  if (!Array.isArray(catalog.products)) throw new Error('catalog release is invalid')
  return catalog.products
    .map((product, index) => mapPublishedProduct(product, index))
    .filter((product): product is CatalogProduct => product !== null)
}
